/**
 * Data migrations (FR-MIG): move or copy a deployment's databases, volumes and configuration to
 * another target — the same environment on another VPS (`server-move`) or another environment of
 * the same project (`environment-copy`). Shared by the server, the web wizard and the CLI.
 */
import type { BackupDTO, JobSummaryDTO, ReleaseDTO } from './api.js';
import type { ConfigLayer } from './config.js';

export const MIGRATION_STATES = ['pending', 'running', 'succeeded', 'failed', 'canceled', 'unknown'] as const;
export type MigrationState = (typeof MIGRATION_STATES)[number];

/** States that hold the migration lock on both targets. */
export const MIGRATION_ACTIVE_STATES: readonly MigrationState[] = ['pending', 'running', 'unknown'];

export const MIGRATION_STEPS = [
  'preflight',
  'verify-backups',
  'copy-config',
  'prepare-destination',
  'quiesce-source',
  'transfer-databases',
  'transfer-volumes',
  'resume-source',
  'start-destination',
  'verify',
  'cutover',
  'finalize',
] as const;
export type MigrationStep = (typeof MIGRATION_STEPS)[number];

export type MigrationKind = 'server-move' | 'environment-copy';
export type MigrationConsistency = 'stop-source' | 'keep-running';
export type ConfigConflictStrategy = 'keep-destination' | 'overwrite-selected';

/** Maximum age of a pre-migration backup accepted by the backup gate. */
export const MIGRATION_BACKUP_MAX_AGE_MINUTES = 60;

export interface MigrationRequest {
  project: string;
  sourceTargetId: string;
  destEnvironment: string;
  destVpsId: string;
  include: {
    /** Database services (manifest `persistence.databases[].service`). */
    databases: string[];
    /** Volume names (manifest `persistence.volumes[].name`) that are not database volumes. */
    volumes: string[];
    config: {
      variables: string[];
      files: string[];
      vpsOverrides: boolean;
      targetOverrides: boolean;
    };
  };
  configConflict: ConfigConflictStrategy;
  /** With `overwrite-selected`: destination values replaced by the source value. */
  overwriteVariables?: string[];
  consistency: MigrationConsistency;
  /** Server moves only: move hostnames to the destination and leave the source stopped. */
  cutover: boolean;
  /** Required when the destination already holds data for a selected database or volume. */
  overwriteDestination: boolean;
  /** Ids from `MigrationPlanDTO.requiredAcknowledgements` the user ticked. */
  acknowledgements: string[];
}

export interface MigrationSubmitRequest extends MigrationRequest {
  planHash: string;
  confirm: string;
  sourceBackupIds: string[];
  destBackupIds: string[];
}

export type DataStrategy = 'logical-dump' | 'cold-copy';

export interface MigrationDatabasePlan {
  service: string;
  type: string;
  volume: string;
  strategy: DataStrategy;
  /** Services stopped while this database is copied. */
  stopsServices: string[];
  destinationHasData: boolean;
  lastSourceBackup?: BackupDTO;
  lastDestinationBackup?: BackupDTO;
}

export interface MigrationVolumePlan {
  name: string;
  strategy: 'cold-copy';
  /** Services that mount the volume; stopped while it is copied. */
  stopsServices: string[];
  destinationHasData: boolean;
  sizeBytes?: number;
}

export interface MigrationConfigVariablePlan {
  name: string;
  secret: boolean;
  kind: 'value' | 'binding';
  layer: ConfigLayer;
  declared: boolean;
  destination: 'missing' | 'same' | 'differs';
}

export interface MigrationConfigFilePlan {
  name: string;
  destination: 'missing' | 'same' | 'differs';
}

export interface MigrationPlanDTO {
  kind: MigrationKind;
  project: { key: string; name: string };
  source: {
    targetId: string;
    environment: string;
    stack: string;
    vps: { id: string; name: string };
    release: ReleaseDTO | null;
    running: boolean;
  };
  destination: {
    targetId: string | null;
    environment: string;
    stack: string;
    vps: { id: string; name: string };
    exists: boolean;
    deployed: boolean;
    hasData: boolean;
    release: ReleaseDTO | null;
    existingVolumes: string[];
  };
  databases: MigrationDatabasePlan[];
  volumes: MigrationVolumePlan[];
  config: {
    /** False for server moves: environment-scoped values are already shared. */
    copiesEnvironmentValues: boolean;
    variables: MigrationConfigVariablePlan[];
    files: MigrationConfigFilePlan[];
    overrides: { scope: 'vps' | 'target'; name: string; to: string }[];
    notCopied: string[];
  };
  credentials: { variable: string; database: string; action: 'copy-source' | 'keep-destination' | 'generate' }[];
  routes: { hostname: string; action: 'generate-new' | 'move-at-cutover' | 'not-routed' }[];
  disk: { sourceFreeBytes: number | null; destinationFreeBytes: number | null; requiredBytes: number | null };
  agents: { source: { online: boolean; supported: boolean }; destination: { online: boolean; supported: boolean } };
  steps: { name: MigrationStep; skipped: boolean; description: string }[];
  requiredBackups: { side: 'source' | 'destination'; targetId: string; database: string; reason: string }[];
  requiredAcknowledgements: { id: string; text: string }[];
  estimatedDowntime: 'none' | 'volume-owners' | 'full';
  blockers: string[];
  warnings: string[];
  confirmPhrase: string;
  planHash: string;
  canMigrate: boolean;
}

export interface MigrationArtifactDTO {
  id: string;
  kind: 'pg-dump' | 'volume-tar';
  name: string;
  state: 'uploading' | 'stored' | 'imported' | 'failed' | 'expired';
  sizeBytes?: number;
  sha256?: string;
  createdAt: string;
}

export interface ConfigCopyReport {
  copied: string[];
  skippedExisting: string[];
  overwritten: string[];
  remapped: string[];
  files: string[];
  credentials: { variable: string; action: 'copy-source' | 'keep-destination' | 'generate' }[];
}

export interface MigrationStepRecord {
  state: 'pending' | 'running' | 'succeeded' | 'failed' | 'skipped' | 'canceled';
  at?: string;
  message?: string;
  childJobIds?: string[];
}

export interface MigrationDTO {
  id: string;
  kind: MigrationKind;
  state: MigrationState;
  project: { key: string; name: string };
  source: { targetId: string; environment: string; vps: { id: string; name: string } };
  destination: { targetId: string | null; environment: string; vps: { id: string; name: string } };
  options: MigrationRequest;
  plan: MigrationPlanDTO;
  steps: Partial<Record<MigrationStep, MigrationStepRecord>>;
  configReport?: ConfigCopyReport;
  sourceBackupIds: string[];
  destBackupIds: string[];
  sourceStopped: boolean;
  artifacts: MigrationArtifactDTO[];
  jobId: string | null;
  childJobs: JobSummaryDTO[];
  createdBy: string;
  createdAt: string;
  finishedAt?: string;
  error?: string;
}
